// src/utils/juniorLanding.js
//
// R-TRAINING slice 1 §9.3 (T9) — A JUNIOR KEEPER LANDS ON FROGGY U.
//
// Only the DEFAULT landing changes: when sign-in would go to
// `AFTER_LOGIN_PATH` (no `from`, no `?redirect=`), a user whose ONLY
// facility membership is Staff asks that facility's `froggy-u/` — the
// server answers `landing: true` only for the caller's own junior row —
// and goes to `/facilities/<id>/froggy-u` when it says so. Every other
// user, every other target, and any failure keep the target they had: a
// junior flag the page cannot read is never a reason to strand anyone.
//
// The junior flag never travels with the login response (T1, the
// exposure rule); it is read here, from the one endpoint that answers it
// for a Staff user about themselves.
//
// R-TRAINING junior Cowork fixes (ruled 2026-11-04):
//   - fix 3: a signed-in junior who first loads the app at exactly `/`
//     lands on Froggy U too (`components/layout/JuniorHomeLanding.jsx`),
//     ONCE per browser session — `froggerKeys.juniorLanded`, set here
//     whenever a junior is landed, so the storefront logo still reaches
//     the store afterwards;
//   - fix 4: the facility sidebar asks `useIsJuniorAt` — THIS file's
//     reading of the same `froggy-u/` `landing` flag, never a second
//     derivation.
import { useEffect, useState } from 'react';
import { AFTER_LOGIN_PATH } from '../constants/routes';
import { getFroggyU } from '../services/api';
import { useSignedInUserId } from '../context/AuthContext';
import { froggerKeys, userIdOf, userPart } from './froggerStorageKeys';

/** The roles the login just stored, when the context has none yet. */
const storedRoles = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem('facilityUserRoles') || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

/**
 * The facility a junior might land in: the user's ONE facility, where
 * every row they hold is Staff (an Owner/Manager row is never a junior).
 * Otherwise null.
 */
export const juniorCandidateFacility = (roles) => {
  const rows = (Array.isArray(roles) && roles.length ? roles : storedRoles())
    .filter((r) => r && r.facility);
  const facilities = Array.from(new Set(rows.map((r) => String(r.facility))));
  if (facilities.length !== 1) return null;
  if (!rows.every((r) => r.role === 'Staff')) return null;
  return facilities[0];
};

/** The user the login just stored, when the caller has no context yet. */
const storedUserId = () => {
  try {
    return userIdOf(JSON.parse(localStorage.getItem('user') || 'null'));
  } catch (e) {
    return null;
  }
};

/** Has this browser session already landed `userId` on Froggy U? */
export const juniorLandedThisSession = (userId) => {
  try {
    return sessionStorage.getItem(froggerKeys.juniorLanded(userId)) === '1';
  } catch (e) {
    return false;
  }
};

/** Remember (for this browser session) that `userId` was landed. */
export const markJuniorLanded = (userId) => {
  try { sessionStorage.setItem(froggerKeys.juniorLanded(userId), '1'); } catch (e) { /* private */ }
};

/**
 * Froggy U's path for a junior, or null for everyone else (and on any
 * failure — a flag the page cannot read never strands anyone).
 */
export async function juniorLandingPath(roles, fetchFroggyU = getFroggyU) {
  const facilityId = juniorCandidateFacility(roles);
  if (!facilityId) return null;
  try {
    const data = await fetchFroggyU(facilityId);
    return data && data.landing === true ? `/facilities/${facilityId}/froggy-u` : null;
  } catch (err) {
    console.warn('[juniorLanding] froggy-u could not be read; keeping the default landing', err);
    return null;
  }
}

/**
 * Where to go after sign-in: `target`, or Froggy U for a junior whose
 * target was the default landing. Never throws. A junior landed here is
 * marked landed for the session (`userId`, else the user the login just
 * stored), so the first-route landing does not bounce them a second time.
 */
export async function landingFor(target, roles, fetchFroggyU = getFroggyU, userId) {
  if (target !== AFTER_LOGIN_PATH) return target;
  const path = await juniorLandingPath(roles, fetchFroggyU);
  if (!path) return target;
  markJuniorLanded(userId === undefined ? storedUserId() : userId);
  return path;
}

// ── fix 4: IS THE SIGNED-IN MEMBER A JUNIOR AT THIS FACILITY? ──────────
//
// Only a STAFF row can be a junior (an Owner/Manager row never is, and a
// user with no row here is not a member) — so every other role answers
// `false` at once, with no request: Owners, Managers and the no-role case
// are exactly as they were. A Staff member asks `froggy-u/` (the SAME
// `landing` flag sign-in reads) and is UNKNOWN (null) until it answers;
// a failed read answers `false` — the sidebar as it was before the fix,
// never a stranded keeper. One answer per user × facility per page load.
const juniorCache = new Map();

/** For tests: forget every cached answer. */
export const resetJuniorCache = () => juniorCache.clear();

const cacheKey = (userId, facilityId) => `${userPart(userId)}|${facilityId}`;

/** `true` / `false`; a promise of one for a Staff row not yet asked. */
export function isJuniorAt(userId, facilityId, role, fetchFroggyU = getFroggyU) {
  if (!facilityId || role !== 'Staff') return false;
  const key = cacheKey(userId, facilityId);
  if (juniorCache.has(key)) return juniorCache.get(key);
  const asked = Promise.resolve()
    .then(() => fetchFroggyU(facilityId))
    .then((data) => Boolean(data && data.landing === true))
    .catch((err) => {
      console.warn('[juniorLanding] froggy-u could not be read; treating as not junior', err);
      return false;
    })
    .then((answer) => { juniorCache.set(key, answer); return answer; });
  juniorCache.set(key, asked);
  return asked;
}

/** What is known NOW, without asking: `true` / `false`, or null. */
export function peekJuniorAt(userId, facilityId, role) {
  if (!facilityId || role !== 'Staff') return false;
  const known = juniorCache.get(cacheKey(userId, facilityId));
  return typeof known === 'boolean' ? known : null;
}

/**
 * The hook: `true` / `false`, or `null` while a Staff member's flag is
 * being read (callers hide what a junior must not see until it is known).
 */
export function useIsJuniorAt(facilityId, role) {
  const userId = useSignedInUserId();
  const [, setAnswered] = useState(0);
  useEffect(() => {
    const now = isJuniorAt(userId, facilityId, role);
    if (typeof now === 'boolean') return undefined;
    let live = true;
    now.then(() => { if (live) setAnswered((n) => n + 1); });
    return () => { live = false; };
  }, [userId, facilityId, role]);
  return peekJuniorAt(userId, facilityId, role);
}
